import React, { useEffect, useState } from "react";
import { Modal, Button, Form, InputGroup, Col } from "react-bootstrap";
import CascadeGroupTreeView from "../../../../components/CascadeGroupTreeView";
import useHandleGroupSearch from "../../../deviceManagement/useHandleGroupSearch";
import { getAllNodeValues } from "../../../../components/treeView/util";
import { IGroupAlerts, ISelectGroupState } from "../../../../common/types/alertsTypes";
import { SearchIcon } from "../../../../components/ui/icons";
import { ITreeNode } from "../../../../common/types/commonTypes";
import "../quickAlerts.scss"

interface IProps {
  show: boolean;
  onConfirm: (updatedGroups: IGroupAlerts[]) => void;
  onCancel: () => void;
  alertTitle: string;
  initialGroups: IGroupAlerts[];
}

const SendAlertConfirmationModal: React.FC<IProps> = ({
  show,
  onConfirm,
  onCancel,
  alertTitle,
  initialGroups,
}) => {

  const { handleGroupInputChange, groupSearchQuery, nodesToDisplay, searchExpandedValues = [],} =
    useHandleGroupSearch({ isEditable: false });
  const checkedIds = initialGroups.map((g) => String(g.id));
  const [selectGroupState, setSelectGroupState] =
    useState<ISelectGroupState>({
      groups: initialGroups,
      checked: checkedIds,
      selectAllChecked: false,
      expanded: [],
    });

  const userExpandedValues = selectGroupState?.expanded || [];

  const expandedValues = React.useMemo(() => {
    if (!groupSearchQuery.trim()) {
      return userExpandedValues;
    }
    return Array.from(
      new Set([...userExpandedValues, ...searchExpandedValues])
    );
  }, [userExpandedValues, searchExpandedValues, groupSearchQuery]);

  const setExpandedValues = (newExpanded: string[]) => {
    if (!groupSearchQuery.trim()) {
      setSelectGroupState({
        ...selectGroupState,
        expanded: newExpanded,
      });
    } else {
      const newExpandedWithoutSearch = newExpanded.filter(
        (val) => !searchExpandedValues.includes(val)
      );
      setSelectGroupState({
        ...selectGroupState,
        expanded: newExpandedWithoutSearch,
      });
    }
  };
  
  const [placedHolderGroupValues, setPlacedHolderGroupValues] =
    useState<string>(initialGroups.length > 0
      ? `(${initialGroups.length}) Groups are Selected`
      : "Select Value");
  const [selectedGroups, setSelectedGroups] = useState<IGroupAlerts[]>(initialGroups);


  useEffect(() => {
  if (!show) {
    return;
  }

  const updatedCheckedIds = initialGroups.map((group) => String(group.id));

  setSelectGroupState({
    groups: initialGroups,
    checked: updatedCheckedIds,
    selectAllChecked: false,
  });

  setSelectedGroups(initialGroups);

  setPlacedHolderGroupValues(
    initialGroups.length > 0
      ? `(${initialGroups.length}) Groups are Selected`
      : "Select Value"
  );
}, [initialGroups, show]);


  const handleDataFromTreeView = (groups: IGroupAlerts[]) => {

  setSelectedGroups(groups);
  setPlacedHolderGroupValues(
    groups.length > 0
      ? `(${groups.length}) Groups are Selected`
      : "Select Value"
  );
};

  const handleSend = () => {
    onConfirm(selectedGroups);
  };

  const isSendDisabled = selectedGroups.length === 0;

  return (
    <Modal
      show={show}
      onHide={onCancel}
      className="modal-container"
      dialogClassName="modal-container"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="delete-heading">
            <h1>Confirm Send Alert</h1>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Are you sure you want to send <b>{alertTitle}</b> alert?
        </p>

        <div className="select-group-container">
          <div className="send-alerts-group-content">
            <label className="heading">Send Alerts to Group(s)</label>
            <div className="alerts-search-and-tree">
              <div className="alert-search-input">
                <InputGroup>
                  <Form.Control
                    placeholder={placedHolderGroupValues}
                    aria-label="Search"
                    aria-describedby="alert-search-input"
                    value={groupSearchQuery}
                    onChange={handleGroupInputChange}
                  />
                  <InputGroup.Text id="alert-search-input">
                    <SearchIcon />
                  </InputGroup.Text>
                </InputGroup>
              </div>
              <div className="alert-checkbox">
                <div className="select-all-checkbox">
                  <CascadeGroupTreeView
                    nodes={nodesToDisplay}
                    expandedValues={expandedValues}
                    setExpandedValues={setExpandedValues}
                    onDataFromTreeView={handleDataFromTreeView}
                    selectGroupState={selectGroupState}
                    setSelectGroupState={setSelectGroupState}
                    isSelectAllEnabled={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button className="btn-cancel" onClick={onCancel}>
          Cancel
        </Button>
        <Button className="btn-style alert-confirm-btn" disabled={isSendDisabled} onClick={handleSend}>
          Send
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SendAlertConfirmationModal;
